import Api from '../../ApiConfig';
import {connectToSocket, disconnectFromSocket} from 'shared/Socket';

const REFRESH_TOKEN_KEY = 'refreshToken';

export function setRefreshToken(token: string | null) {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export function setAuthToken(token: string | null) {
  if (token) {
    Api.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem('token', token);
    connectToSocket(token);
  } else {
    delete Api.defaults.headers.common.Authorization;
    localStorage.removeItem('token');
    setRefreshToken(null);
    disconnectFromSocket();
  }
};

// Single-flight refresh: if several requests 401 at once, only one /auth/refresh call is
// made and the rest are queued to be retried once it resolves.
let isRefreshing = false;
let pendingQueue: Array<{resolve: (token: string) => void; reject: (err: any) => void}> = [];

function resolveQueue(token: string) {
  pendingQueue.forEach(p => p.resolve(token));
  pendingQueue = [];
};

function rejectQueue(err: any) {
  pendingQueue.forEach(p => p.reject(err));
  pendingQueue = [];
};

Api.interceptors.response.use(
  res => res,
  (err: any) => {
    const unauthorized = err?.response?.status === 401 || err?.response?.statusText === 'Unauthorized' || err?.response?.data?.statusCode === 401 || err?.response?.data?.message === 'Unauthorized';
    if (!unauthorized) {
      return Promise.reject(err);
    }

    const originalRequest = err.config;
    // No request config to retry, the request was already retried once, or this is the
    // refresh call itself failing => don't loop, just sign out.
    if (!originalRequest || originalRequest._retry || originalRequest._isRefreshRequest) {
      console.log('Api.interceptors: Unauthorized => Logout user');
      dispatchEventAuthSignout();
      return Promise.reject(err);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.log('Api.interceptors: Unauthorized (no refresh token) => Logout user');
      dispatchEventAuthSignout();
      return Promise.reject(err);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (newToken: string) => {
            originalRequest._retry = true;
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(Api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    return Api.post('/auth/refresh', {token: refreshToken}, {_isRefreshRequest: true} as any)
      .then(res => {
        const {accessToken, refreshToken: newRefreshToken} = res.data;
        setAuthToken(accessToken);
        setRefreshToken(newRefreshToken);
        isRefreshing = false;
        resolveQueue(accessToken);

        originalRequest._retry = true;
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return Api(originalRequest);
      })
      .catch(refreshErr => {
        isRefreshing = false;
        rejectQueue(refreshErr);
        console.log('Api.interceptors: Refresh failed => Logout user');
        dispatchEventAuthSignout();
        return Promise.reject(refreshErr);
      });
  },
);

export function dispatchEventAuthSignout() {
  document.dispatchEvent(new CustomEvent('AuthSignout'));
};

export default Api;
